import type Luminary from '@lumen5/luminary';
import type { SceneGroup } from '@lumen5/luminary';

import { computeShortIds, defaultShortId, SceneResolutionError } from './scene-addressing';

import { getSceneGroupType } from 'js/src/agent/video-state/serialization/assetSerializer';

/** Short, git-style scene-group handle length; lengthened per-id on collision (see computeShortIds). */
export const SHORT_SCENE_GROUP_ID_LENGTH = 6;

export function computeSceneGroupShortIds(sceneGroupIds: string[]): Map<string, string> {
    return computeShortIds(sceneGroupIds, SHORT_SCENE_GROUP_ID_LENGTH);
}

export function defaultShortSceneGroupId(sceneGroupId: string): string {
    return defaultShortId(sceneGroupId, SHORT_SCENE_GROUP_ID_LENGTH);
}

/** The short scene_group_id handle to show in tool messages — matches the video state, collision-safe. */
export function sceneGroupDisplayId(luminary: Luminary, sceneGroupId: string): string {
    return (
        computeSceneGroupShortIds(luminary.sceneGroups.map((group) => group.id)).get(
            sceneGroupId,
        ) ?? defaultShortSceneGroupId(sceneGroupId)
    );
}

export type SceneGroupByIdResolution =
    | { sceneGroup: SceneGroup; index: number }
    | { error: string };

/** Resolve a scene group by short or full scene_group_id; fail-closed on zero or ambiguous matches. */
export function resolveSceneGroupById(
    luminary: Luminary,
    sceneGroupId: string,
): SceneGroupByIdResolution {
    const normalized = sceneGroupId.trim().toLowerCase();
    const matches = luminary.sceneGroups
        .map((sceneGroup, index) => ({ sceneGroup, index }))
        .filter(({ sceneGroup }) => sceneGroup.id.toLowerCase().startsWith(normalized));

    if (matches.length === 0) {
        return {
            error: `No scene group found with scene_group_id "${sceneGroupId}". Re-read the current video state and use a scene_group_id exactly as shown there.`,
        };
    }
    if (matches.length > 1) {
        const shortIds = computeSceneGroupShortIds(luminary.sceneGroups.map((group) => group.id));
        const candidates = matches
            .map(
                ({ sceneGroup }) =>
                    `  - scene_group_id ${shortIds.get(sceneGroup.id)} (${getSceneGroupType(sceneGroup)})`,
            )
            .join('\n');
        return {
            error: `scene_group_id "${sceneGroupId}" is ambiguous — it matches ${matches.length} scene groups:\n${candidates}\nPass a longer scene_group_id to pick one.`,
        };
    }
    return matches[0];
}

export interface SceneGroupHandleArgs {
    scene_group_id?: string;
}

/** Resolve a scene group by its stable scene_group_id; throws {@link SceneResolutionError} on a missing/bad id. */
export function resolveSceneGroupHandle(
    luminary: Luminary,
    args: SceneGroupHandleArgs,
): { sceneGroup: SceneGroup; index: number; sceneGroupShortId: string } {
    const { scene_group_id } = args;
    if (scene_group_id == null || scene_group_id.trim() === '') {
        throw new SceneResolutionError(
            'Provide scene_group_id (the stable scene group id from the video state) to identify the scene group.',
        );
    }
    const resolved = resolveSceneGroupById(luminary, scene_group_id);
    if ('error' in resolved) {
        throw new SceneResolutionError(resolved.error);
    }
    return {
        ...resolved,
        sceneGroupShortId: sceneGroupDisplayId(luminary, resolved.sceneGroup.id),
    };
}

/** Insert slot from an optional after_scene_group_id anchor: omitted → 0 (start), else anchor index + 1. Throws on an unknown anchor. */
export function resolveInsertSlotAfter(luminary: Luminary, afterSceneGroupId?: string): number {
    if (afterSceneGroupId == null || afterSceneGroupId.trim() === '') {
        return 0;
    }
    const resolved = resolveSceneGroupById(luminary, afterSceneGroupId);
    if ('error' in resolved) {
        throw new SceneResolutionError(resolved.error);
    }
    return resolved.index + 1;
}
